import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { PrinterIcon } from 'lucide-react';
import { usePricing } from '../../contexts/PricingContext';
import { PartnershipBrief } from './PartnershipBrief';
interface BriefPrintButtonProps {
  className: string;
  /** Base of the suggested PDF file name; the company name is appended. */
  fileTitle?: string;
  /** The document to print. Defaults to the Partnership Brief. */
  renderDocument?: () => React.ReactNode;
}

/**
 * Prints a two-page brief as a clean Letter PDF. A fresh copy of the document
 * is mounted directly under <body>, outside the deck's frames, transforms and
 * navigation, and only that copy is printed.
 */
export function BriefPrintButton({
  className,
  fileTitle = 'D2D HIRE Partnership Brief',
  renderDocument = () => <PartnershipBrief data-id="element-784" data-mp="2txwpyvpnj7sr" />
}: BriefPrintButtonProps) {
  const {
    companyName
  } = usePricing();
  // Holds the PDF title while printing; null when idle.
  const [printTitle, setPrintTitle] = useState<string | null>(null);
  const printing = printTitle !== null;
  const startPrint = () => {
    const name = companyName.trim();
    setPrintTitle(name ? `${fileTitle} - ${name}` : fileTitle);
  };
  useEffect(() => {
    if (printTitle === null) return;
    const previousTitle = document.title;
    // The browser uses the page title as the suggested PDF file name.
    document.title = printTitle;
    document.body.classList.add('brief-printing');
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      document.body.classList.remove('brief-printing');
      document.title = previousTitle;
      setPrintTitle(null);
    };
    window.addEventListener('afterprint', finish);

    // Wait for fonts, then two frames so the print copy has measured and
    // applied its shrink-to-fit before the print dialog snapshots it.
    document.fonts.ready.then(() => {
      requestAnimationFrame(() => requestAnimationFrame(() => {
        if (!done) window.print();
      }));
    });
    return () => {
      window.removeEventListener('afterprint', finish);
      if (!done) {
        done = true;
        document.body.classList.remove('brief-printing');
        document.title = previousTitle;
      }
    };
  }, [printTitle]);
  return <>
      <button type="button" onClick={startPrint} disabled={printing} className={className} data-id="element-785" data-mp="2t8ys7wjdpw5">
        <PrinterIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-786" data-mp="3ba419m9f989p" />
        {printing ? 'Preparing…' : 'Save PDF'}
      </button>
      {printing && createPortal(<div className="brief-root brief-print-portal" aria-hidden="true" data-id="element-787" data-mp="3kaio4q21avfb">
            {renderDocument()}
          </div>, document.body)}
    </>;
}